import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStudio } from "../../../context/Studio";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import { CalculatePriceModal } from "../../CalculatePriceModal";
import { Box } from "../../../common/components";
import GradientChip from "../../../common/components/Chip/GradientChip";
import StudioNavButton from "./StudioNavButton";
import { DesignPriceBrackets } from "../../../types";
import PricingService from "../../../service/PricingService";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { Skeleton } from "@mui/material";

const StudioPriceModalButton = () => {
  const {
    designState: { designPricing },
    designId,
    productName,
  } = useStudio();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const { isMobile } = useThemeBreakpoints();
  const { getCurrency } = useCurrency();
  const toggleModal = () => setModalOpen((prev) => !prev);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricing, setPricing] = useState<DesignPriceBrackets>();

  useEffect(() => {
    const fetchPricing = async () => {
      setPricingLoading(true);
      const pricing = await PricingService.getDesignPricing(
        designId,
        getCurrency()
      );
      setPricing(pricing);
      setPricingLoading(false);
    };
    fetchPricing();
  }, [designId, getCurrency]);

  const creditsText = () => {
    if (!designPricing || !pricing) return "";

    if (designPricing?.creditsUsedInDesign > pricing.freeCredits) {
      const count = designPricing.creditsUsedInDesign - pricing.freeCredits;
      return !isMobile
        ? t("studio.toolbar.extraCredits", {
            count,
          })
        : `+${count}`;
    }
    return !isMobile
      ? t("studio.toolbar.credits", {
          count: designPricing?.creditsUsedInDesign,
        })
      : designPricing?.creditsUsedInDesign.toString();
  };

  if (pricingLoading || !pricing) {
    return (
      <Skeleton
        variant="text"
        sx={{ width: { xs: 50, lg: 125 } }}
        height={32}
      />
    );
  }

  return (
    <>
      <StudioNavButton
        icon="kid_star"
        hoverAndActiveIcon="family_star"
        title={
          isMobile ? (
            creditsText()
          ) : (
            <Box display={"flex"} gap={"4px"} alignItems={"center"}>
              {t("general.pricing")}
              <GradientChip>{creditsText()}</GradientChip>
            </Box>
          )
        }
        onClick={toggleModal}
      />
      {productName && (
        <CalculatePriceModal
          open={modalOpen}
          onClose={toggleModal}
          designId={designId}
          productName={productName}
        />
      )}
    </>
  );
};

export default memo(StudioPriceModalButton);
